// Aircraft profile data model — v2.
//
// ForeFlight-class profile: General / Avionics / Performance / Glide /
// Altitudes / Weights / Weight & Balance (multi-profile, with stations
// and a polygonal CG envelope) / Fuel / Filing / Dinghy / Emergency /
// Ownship.
//
// Migration from v1 (the old simple shape) is handled in
// aircraftStore.ts. New code should use AircraftProfile from here.
//
// All angles in DEGREES. All distances default to INCHES from the
// aircraft datum (typically firewall or LE root). Weights default to
// POUNDS. The user can switch units in the UI; the stored values are
// always in the canonical (lb, in, gal) form so cross-aircraft math is
// trivial.

// ───── Enumerations (kept as string literal unions for type safety) ─────

export type AircraftCategory =
  | 'Airplane'
  | 'Helicopter'
  | 'Glider'
  | 'Balloon'
  | 'Powered-lift'
  | 'Gyroplane'
  | 'Other';

export type AirspeedUnits = 'KIAS' | 'KTAS' | 'MPH';
export type LengthUnits = 'Inches' | 'Centimeters';
export type WeightUnits = 'Pounds' | 'Kilograms';
export type FuelUnits = 'Gallons' | 'Liters' | 'Pounds' | 'Kilograms';

export type FuelType =
  | '100LL'
  | '100/130'
  | '94UL'
  | '91UL'
  | 'Mogas'
  | 'Jet A'
  | 'Jet A-1'
  | 'Diesel'
  | 'Other';

/** Default density (lb / US gallon) for a given fuel type. */
export const FUEL_DENSITY_LB_PER_GAL: Record<FuelType, number> = {
  '100LL':    6.0,
  '100/130':  6.0,
  '94UL':     6.0,
  '91UL':     6.0,
  'Mogas':    6.0,
  'Jet A':    6.7,
  'Jet A-1':  6.7,
  'Diesel':   7.0,
  'Other':    6.0,
};

export type ReservePolicy =
  | 'Manual (Minutes)'
  | 'Manual (Gallons)'
  | 'FAA Day VFR (30 min)'
  | 'FAA Night VFR (45 min)'
  | 'FAA IFR (45 min)';

export type OwnershipStatus =
  | 'Sole owner'
  | 'Co-owned'
  | 'Renter'
  | 'Operator'
  | 'Flight school';

export type WakeCategory = 'L' | 'M' | 'H' | 'J';

// ICAO equipment / surveillance / PBN are multi-select chip lists;
// we keep them as plain string arrays so adding new codes (the FAA
// updates these every couple years) requires zero code changes.

export interface IcaoEquipment {
  /** e.g. ['B', 'G', 'R'] — see ICAO Doc 4444 Item 10a. */
  equipment: string[];
  /** e.g. ['B2', 'E'] — Item 10b transponder/surveillance. */
  surveillance: string[];
  /** e.g. ['B2', 'C2', 'D2', 'O2', 'S1'] — Item 18 PBN. */
  pbn: string[];
  /** Wake category — Item 9. */
  wake: WakeCategory;
  /** Free text Item 18 OTHER. */
  other?: string;
  /** Free text Item 18 STS. */
  sts?: string;
  /** Free text Remarks (RMK). */
  remarks?: string;
}

export const ICAO_EQUIPMENT_CODES: { code: string; label: string }[] = [
  { code: 'N', label: 'No COM/NAV equipment' },
  { code: 'S', label: 'Std (VHF, VOR, ILS)' },
  { code: 'A', label: 'GBAS landing system' },
  { code: 'B', label: 'LPV (SBAS)' },
  { code: 'C', label: 'LORAN C' },
  { code: 'D', label: 'DME' },
  { code: 'E1', label: 'FMC WPR ACARS' },
  { code: 'E2', label: 'D-FIS ACARS' },
  { code: 'E3', label: 'PDC ACARS' },
  { code: 'F', label: 'ADF' },
  { code: 'G', label: 'GNSS / GPS' },
  { code: 'H', label: 'HF RTF' },
  { code: 'I', label: 'Inertial navigation' },
  { code: 'J1', label: 'CPDLC ATN VDL Mode 2' },
  { code: 'J2', label: 'CPDLC FANS 1/A HFDL' },
  { code: 'J3', label: 'CPDLC FANS 1/A VDL Mode A' },
  { code: 'J4', label: 'CPDLC FANS 1/A VDL Mode 2' },
  { code: 'J5', label: 'CPDLC FANS 1/A SATCOM (INMARSAT)' },
  { code: 'J6', label: 'CPDLC FANS 1/A SATCOM (MTSAT)' },
  { code: 'J7', label: 'CPDLC FANS 1/A SATCOM (Iridium)' },
  { code: 'K', label: 'MLS' },
  { code: 'L', label: 'ILS' },
  { code: 'M1', label: 'ATC SATVOICE (INMARSAT)' },
  { code: 'M2', label: 'ATC SATVOICE (MTSAT)' },
  { code: 'M3', label: 'ATC SATVOICE (Iridium)' },
  { code: 'O', label: 'VOR' },
  { code: 'P1', label: 'CPDLC RCP 400' },
  { code: 'P2', label: 'CPDLC RCP 240' },
  { code: 'P3', label: 'SATVOICE RCP 400' },
  { code: 'R', label: 'PBN approved' },
  { code: 'T', label: 'TACAN' },
  { code: 'U', label: 'UHF RTF' },
  { code: 'V', label: 'VHF RTF' },
  { code: 'W', label: 'RVSM approved' },
  { code: 'X', label: 'MNPS approved' },
  { code: 'Y', label: 'VHF 8.33 kHz' },
  { code: 'Z', label: 'Other (see Item 18)' },
];

export const ICAO_SURVEILLANCE_CODES: { code: string; label: string }[] = [
  { code: 'N', label: 'No surveillance equipment' },
  { code: 'A', label: 'Mode A transponder' },
  { code: 'C', label: 'Mode A + altitude (Mode C)' },
  { code: 'E', label: 'Mode S — incl. ID, altitude, ext. squitter (ADS-B)' },
  { code: 'H', label: 'Mode S — incl. ID, altitude, enhanced surveillance' },
  { code: 'I', label: 'Mode S — incl. ID, no altitude' },
  { code: 'L', label: 'Mode S — full (ID, altitude, ext. squitter, enhanced)' },
  { code: 'P', label: 'Mode S — incl. altitude, no ID' },
  { code: 'S', label: 'Mode S — incl. altitude and ID' },
  { code: 'X', label: 'Mode S — no ID, no altitude' },
  { code: 'B1', label: 'ADS-B 1090 MHz "out"' },
  { code: 'B2', label: 'ADS-B 1090 MHz "out" and "in"' },
  { code: 'U1', label: 'ADS-B UAT "out"' },
  { code: 'U2', label: 'ADS-B UAT "out" and "in"' },
  { code: 'V1', label: 'ADS-B VDL Mode 4 "out"' },
  { code: 'V2', label: 'ADS-B VDL Mode 4 "out" and "in"' },
  { code: 'D1', label: 'ADS-C FANS 1/A' },
  { code: 'G1', label: 'ADS-C ATN' },
];

export const ICAO_PBN_CODES: { code: string; label: string }[] = [
  { code: 'A1', label: 'RNAV 10 (RNP 10)' },
  { code: 'B1', label: 'RNAV 5 (all permitted sensors)' },
  { code: 'B2', label: 'RNAV 5 GNSS' },
  { code: 'B3', label: 'RNAV 5 DME/DME' },
  { code: 'B4', label: 'RNAV 5 VOR/DME' },
  { code: 'B5', label: 'RNAV 5 INS or IRS' },
  { code: 'B6', label: 'RNAV 5 LORAN C' },
  { code: 'C1', label: 'RNAV 2 (all permitted sensors)' },
  { code: 'C2', label: 'RNAV 2 GNSS' },
  { code: 'C3', label: 'RNAV 2 DME/DME' },
  { code: 'C4', label: 'RNAV 2 DME/DME/IRU' },
  { code: 'D1', label: 'RNAV 1 (all permitted sensors)' },
  { code: 'D2', label: 'RNAV 1 GNSS' },
  { code: 'D3', label: 'RNAV 1 DME/DME' },
  { code: 'D4', label: 'RNAV 1 DME/DME/IRU' },
  { code: 'L1', label: 'RNP 4' },
  { code: 'O1', label: 'Basic RNP 1 (all permitted sensors)' },
  { code: 'O2', label: 'Basic RNP 1 GNSS' },
  { code: 'O3', label: 'Basic RNP 1 DME/DME' },
  { code: 'O4', label: 'Basic RNP 1 DME/DME/IRU' },
  { code: 'S1', label: 'RNP APCH' },
  { code: 'S2', label: 'RNP APCH with BARO-VNAV' },
  { code: 'T1', label: 'RNP AR APCH with RF (special authorization)' },
  { code: 'T2', label: 'RNP AR APCH without RF (special authorization)' },
];

// ───── Avionics ─────

export interface AvionicsBox {
  id: string;
  /** User label, e.g. "GTN 750" or "G3X PFD" */
  name: string;
  manufacturer?: string;
  model?: string;
  serial?: string;
  /** Quick-pick capabilities so the filing tab can derive sensible
   *  default ICAO equipment codes. */
  capabilities: {
    gps?: boolean;
    waas?: boolean;
    nav?: boolean;
    com?: boolean;
    transponder?: 'none' | 'A' | 'C' | 'S';
    adsbOut?: boolean;
    adsbIn?: boolean;
    fms?: boolean;
    autopilot?: boolean;
    cpdlc?: boolean;
  };
}

// ───── Performance ─────

/** Single power setting × altitude × condition row used to interpolate
 *  TAS and fuel flow. v1 keeps this very simple; later we can hang
 *  multi-row tables off this. */
export interface PerformanceProfile {
  id: string;
  /** Display name e.g. "2300 RPM Max MP", "Long-Range Cruise". */
  name: string;
  /** True airspeed at the chosen power/altitude (knots). */
  cruiseKtas: number;
  /** Fuel flow at that power setting (gallons / hour). */
  fuelBurnGph: number;
  /** Manifold pressure / RPM as a free string for note. */
  power?: string;
  /** Pressure altitude this profile is good for, ft (single-point v1). */
  pressureAltFt?: number;

  /** Climb / descent (single-point v1). */
  climbRateFpm?: number;
  climbKtas?: number;
  climbBurnGph?: number;
  descentRateFpm?: number;
  descentKtas?: number;
  descentBurnGph?: number;

  /** Default profile used by flight planning if one isn't picked. */
  isDefault?: boolean;

  /** Takeoff distance table (POH chart digitized). When present, the
   *  FlightDetailPage Performance section computes TODR for departure
   *  and alternate at the forecast conditions. */
  takeoffDistance?: PerformanceDistanceTable;
  /** Landing distance table. Used for destination + alternate. */
  landingDistance?: PerformanceDistanceTable;
}

export interface PerformanceDistanceCell {
  /** Outside-air temperature in °C. */
  oatC: number;
  /** Ground roll distance in feet. */
  groundRollFt: number;
  /** Distance to clear a 50-ft obstacle in feet. */
  obstacleFt: number;
}

export interface PerformanceDistanceRow {
  /** Pressure altitude in feet MSL. */
  pressureAltFt: number;
  cells: PerformanceDistanceCell[];
}

export interface PerformanceDistanceTable {
  /** Reference weight the published table assumes (lb). */
  weightLb: number;
  /** Flap setting the table is for. */
  flaps?: string;
  /** Power setting. */
  powerSetting?: string;
  /** Pressure-altitude rows, each with one or more OAT cells. */
  rows: PerformanceDistanceRow[];
  /** Headwind correction: subtract this FRACTION per knot of headwind.
   *  E.g. 0.011 = 1.1% per kt (POH C182 typical). */
  perKtHeadwind: number;
  /** Tailwind correction: ADD this fraction per knot of tailwind. */
  perKtTailwind: number;
  /** Surface multipliers applied to both ground roll and obstacle
   *  distance. Keys: 'paved' (always 1.0), 'dryGrass', 'wetGrass',
   *  'wetPaved'. */
  surfaceMultipliers?: Partial<Record<
    'paved' | 'dryGrass' | 'wetGrass' | 'wetPaved',
    number
  >>;
  /** Per-100-lb-above-ref fraction scaler (linear approximation). */
  per100LbAbove?: number;
  /** Free-text source notes — 'POH C182T §5', 'Reference values —
   *  verify against your POH'. */
  source: string;
}

// ───── V-speeds ─────

export interface VSpeeds {
  /** Stall, flaps full (KIAS). */
  vS0?: number;
  /** Stall, flaps up (KIAS). */
  vS1?: number;
  /** Rotation. */
  vR?: number;
  /** Best angle of climb. */
  vX?: number;
  /** Best rate of climb. */
  vY?: number;
  /** Max flap-extended speed. */
  vFE?: number;
  /** Max structural cruising. */
  vNO?: number;
  /** Never exceed. */
  vNE?: number;
  /** Best glide. */
  vGlide?: number;
  /** Maneuvering speed (varies by weight; record at MTOW for now). */
  vA?: number;
  /** Landing gear extended max speed (retracts only). */
  vLE?: number;
  /** Landing gear operating speed (retracts only). */
  vLO?: number;
}

// ───── Weight & Balance ─────

export type StationType = 'crew' | 'pax' | 'baggage' | 'fuel' | 'oil' | 'other';

export interface WBStation {
  id: string;
  /** Display name, e.g. "Pilot", "Front Pax", "Baggage A". */
  name: string;
  /** Arm aft of datum (inches by default). */
  armIn: number;
  type: StationType;
  /** Maximum allowed weight at this station (lbs). Optional. */
  maxLb?: number;
  /** Default weight to pre-fill (lbs). Optional. */
  defaultLb?: number;
  /** For fuel stations: capacity in gallons. */
  capacityGal?: number;
}

/**
 * One vertex of the certified CG envelope. The polygon is the closed
 * loop formed by joining vertices[0..n-1] in order (last → first
 * closes the polygon). All weights in lb, arms in in.
 */
export interface WBEnvelopeVertex {
  weightLb: number;
  cgIn: number;
}

export interface WBProfile {
  id: string;
  /** Display name, e.g. "N3405S Default 11/2023". */
  name: string;
  /** Revision date for the empty-weight figures. */
  revisedAt?: string; // ISO date

  /** Empty weight as currently signed off in the airplane log book. */
  emptyWeightLb: number;
  /** Empty CG / longitudinal arm (inches aft of datum). */
  emptyArmIn: number;

  /** Loadable stations. */
  stations: WBStation[];

  /** Closed polygon of the certified CG envelope. */
  envelope: WBEnvelopeVertex[];

  /** Last-used per-station loads. Persisted whenever a flight's
   *  stationLoads change so the next flight on this profile starts
   *  pre-filled with the same weights (pilot 195 lb, instructor 180
   *  lb, baggage 40 lb, etc.) instead of zeros. Boss request 2026-05-25
   *  21:01 PT: 'store the prior CG values as default values so we
   *  don't have to enter all of the weight over again'.
   *  Falls back to WBStation.defaultLb when missing. */
  defaultStationLoads?: Array<{
    stationId: string;
    weightLb: number;
  }>;

  /** Fuel density used by this profile. Defaults to fuel type density. */
  fuelLbPerGal?: number;

  /**
   * Moment change in inch-pounds when the landing gear is retracted
   * (gear-up minus gear-down). Positive = retraction shifts the CG
   * aft (typical for tricycle gear where the nose gear is forward of
   * the datum and folds aft into the fuselage). Negative = retraction
   * shifts CG forward. Applied to the total moment when the user
   * toggles "Gear up" in the load editor.
   *
   * Defaults to 0 for non-retractable / unknown installations.
   */
  gearRetractionMomentInLb?: number;

  notes?: string;
}

// ───── Fuel ─────

export interface FuelConfig {
  fuelType: FuelType;
  fuelUnits: FuelUnits;
  /** Start + taxi + run-up + takeoff allowance (in fuelUnits). */
  startTaxiTakeoff: number;
  /** Total usable fuel in fuelUnits. */
  totalUsable: number;
  reservePolicy: ReservePolicy;
  /** Reserve value in minutes or gallons depending on policy. */
  reserveValue: number;
}

// ───── Glide / Altitudes ─────

export interface GlidePerf {
  bestGlideKt?: number;
  bestGlideRatio?: number;
}

export interface AltitudesConfig {
  defaultCruiseFt?: number;
  maxCeilingFt?: number;
}

// ───── Emergency / Dinghy / Filing ─────

export type LifeJackets = 'None' | 'With light' | 'With fluorescein' | 'Both';
export type SurvivalKit = 'None' | 'Polar' | 'Desert' | 'Maritime' | 'Jungle';

export interface EmergencyEquip {
  lifeJackets: LifeJackets;
  /** Multi-select: VHF / UHF / ELT / 121.5 / 406. */
  radios: string[];
  survival: SurvivalKit[];
  remarks?: string;
}

export interface DinghyEquip {
  count?: number;
  capacityPersons?: number;
  color?: string;
  covered: boolean;
}

// ───── Filing (FAA + ICAO) ─────

export interface FilingConfig {
  /** Pre-2017 FAA equipment suffix, e.g. "/G", "/A". */
  faaEquipment?: string;
  icao: IcaoEquipment;
}

// ───── Ownship ─────

export type OwnshipMarker =
  | 'high-wing'
  | 'low-wing'
  | 'twin'
  | 'helicopter'
  | 'glider'
  | 'jet'
  | 'arrow'; // generic

// ───── Top-level profile ─────

export interface AircraftProfile {
  id: string;
  schemaVersion: 2;

  /** When set, this aircraft belongs to an organization — every member
   *  of the org can see and edit it. When null, it's a personal
   *  aircraft (owner_id-scoped). Mutable via the 'Share with org' /
   *  'Move to personal' actions on the aircraft detail page. */
  orgId?: string | null;

  // GENERAL ----------------------------------------------------------
  tail: string;
  callSign?: string;
  serialNumber?: string;

  /** Display name, e.g. "182G Skylane". */
  typeLabel?: string;
  /** ICAO type designator, e.g. "C182". */
  icaoType?: string;
  category: AircraftCategory;
  color?: string;
  homeAirport?: string;

  airspeedUnits: AirspeedUnits;
  lengthUnits: LengthUnits;
  enableVirtualWaypoints: boolean;
  ownership: OwnershipStatus;

  // AVIONICS ---------------------------------------------------------
  avionics: AvionicsBox[];

  // PERFORMANCE ------------------------------------------------------
  performanceProfiles: PerformanceProfile[];
  vSpeeds: VSpeeds;
  /** Free-text "Runway profile" filter ForeFlight uses. v1: just store. */
  runwayProfile?: string;

  // GLIDE + ALTITUDES ------------------------------------------------
  glide: GlidePerf;
  altitudes: AltitudesConfig;

  // WEIGHTS (top-level limits — independent of any single W&B profile) -
  weightUnits: WeightUnits;
  basicEmptyLb?: number;
  maxZeroFuelLb?: number;
  maxRampLb?: number;
  maxTakeoffLb?: number;
  maxLandingLb?: number;

  // WEIGHT & BALANCE PROFILES ---------------------------------------
  wbProfiles: WBProfile[];
  /** Currently active W&B profile id. */
  activeWBProfileId?: string;

  // FUEL -------------------------------------------------------------
  fuel: FuelConfig;

  // FILING -----------------------------------------------------------
  filing: FilingConfig;

  // EMERGENCY / DINGHY ----------------------------------------------
  emergency: EmergencyEquip;
  dinghy: DinghyEquip;

  // OWNSHIP ----------------------------------------------------------
  ownshipMarker: OwnshipMarker;

  // META -------------------------------------------------------------
  notes?: string;
  /** Set true for the active default aircraft (PlanPanel uses this). */
  isDefault?: boolean;
}

// ───── Helpers ─────

/** A blank profile with sensible defaults. Used by the New-aircraft
 *  flow before any seed-by-type runs. */
export function emptyAircraftProfile(): Omit<AircraftProfile, 'id'> {
  return {
    schemaVersion: 2,
    tail: '',
    category: 'Airplane',
    airspeedUnits: 'KIAS',
    lengthUnits: 'Inches',
    enableVirtualWaypoints: true,
    ownership: 'Sole owner',
    avionics: [],
    performanceProfiles: [],
    vSpeeds: {},
    glide: {},
    altitudes: {},
    weightUnits: 'Pounds',
    wbProfiles: [],
    fuel: {
      fuelType: '100LL',
      fuelUnits: 'Gallons',
      startTaxiTakeoff: 1.5,
      totalUsable: 0,
      reservePolicy: 'Manual (Minutes)',
      reserveValue: 30,
    },
    filing: {
      faaEquipment: '/G',
      icao: { equipment: ['S', 'G'], surveillance: ['C'], pbn: [], wake: 'L' },
    },
    emergency: { lifeJackets: 'None', radios: ['VHF'], survival: ['None'] },
    dinghy: { covered: false },
    ownshipMarker: 'high-wing',
  };
}
